/**
 * MailboxPage — In-app email client backed by Gmail API
 * Three-panel layout: sidebar | list | detail/compose
 * Theme-aware via useTheme(). Task assignment integration.
 * Spacemancraft Pvt Ltd
 */
import { useState, useEffect, useRef } from 'react';
import { getApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '../appContext';
import { actorStamp } from '../utils/actorStamp';

const db = () => getFirestore(getApp());
const GMAIL = 'https://gmail.googleapis.com/gmail/v1/users/me';

// ── Gmail REST helpers ────────────────────────────────────────────────────────
async function gmailFetch(path, token, opts = {}) {
  const res = await fetch(GMAIL + path, {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts.method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) throw new Error('TOKEN_EXPIRED');
  if (res.status === 204 || (res.status === 200 && res.headers.get('content-length') === '0')) return {};
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

function buildRawEmail({ to, cc, subject, bodyHtml, fromName, fromEmail }) {
  const subjectEncoded = '=?UTF-8?B?' + btoa(unescape(encodeURIComponent(subject))) + '?=';
  const bodyB64 = btoa(unescape(encodeURIComponent(bodyHtml)));
  const lines = [
    'MIME-Version: 1.0',
    `From: ${fromName} <${fromEmail}>`,
    `To: ${to}`,
    ...(cc ? [`Cc: ${cc}`] : []),
    `Subject: ${subjectEncoded}`,
    'Content-Type: text/html; charset="UTF-8"',
    'Content-Transfer-Encoding: base64',
    '', bodyB64,
  ];
  return btoa(lines.join('\r\n')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function extractBody(payload) {
  if (!payload) return '';
  if (payload.body?.data) {
    try { return atob(payload.body.data.replace(/-/g, '+').replace(/_/g, '/')); } catch (_) { return ''; }
  }
  if (payload.parts) {
    const html = payload.parts.find(p => p.mimeType === 'text/html');
    if (html?.body?.data) return atob(html.body.data.replace(/-/g, '+').replace(/_/g, '/'));
    const txt = payload.parts.find(p => p.mimeType === 'text/plain');
    if (txt?.body?.data) return atob(txt.body.data.replace(/-/g, '+').replace(/_/g, '/')).replace(/\n/g, '<br>');
    for (const part of payload.parts) { const r = extractBody(part); if (r) return r; }
  }
  return '';
}

function hdr(headers, name) {
  return (headers || []).find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';
}

function relTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function getInitials(str) {
  if (!str) return '?';
  const name = str.replace(/<.*?>/g, '').trim();
  const parts = name.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function extractEmail(str) {
  const m = (str || '').match(/<(.+?)>/);
  return m ? m[1].toLowerCase() : (str || '').toLowerCase().trim();
}

// ── Token storage ─────────────────────────────────────────────────────────────
const TOKEN_KEY = 'spacemancraft_gmail_token';
function loadStoredToken() {
  try {
    const s = JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}');
    if (s.token && s.expiry > Date.now() + 60_000) return s;
  } catch (_) {}
  return null;
}
function storeToken(token, email) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, email, expiry: Date.now() + 3_500_000 }));
}
function clearToken() { localStorage.removeItem(TOKEN_KEY); }

// ══════════════════════════════════════════════════════════════════════════════
export default function MailboxPage({ user }) {
  const theme = useTheme();
  const T = {
    bg:     theme.bg,
    panel:  theme.panel,
    panel2: theme.cardBg || theme.panel,
    border: theme.border,
    text:   theme.text,
    bright: theme.bright,
    muted:  theme.muted,
    accent: theme.accent,
    green:  '#10b981',
    red:    '#f43f5e',
    yellow: '#f59e0b',
    mono:   theme.monoFont,
    sans:   theme.font,
    dark:   theme.dark,
  };

  const stored = loadStoredToken();
  const [gmailToken,   setGmailToken]   = useState(stored?.token || null);
  const [gmailEmail,   setGmailEmail]   = useState(stored?.email || '');
  const [tab,          setTab]          = useState('inbox'); // inbox | sent | compose
  const [messages,     setMessages]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [viewMsg,      setViewMsg]      = useState(null);
  const [msgBody,      setMsgBody]      = useState('');
  const [loadingBody,  setLoadingBody]  = useState(false);
  const [members,      setMembers]      = useState([]);
  const [connecting,   setConnecting]   = useState(false);
  const [nextToken,    setNextToken]    = useState(null);
  const [search,       setSearch]       = useState('');
  const [isMobile,     setIsMobile]     = useState(() => window.innerWidth < 768);
  const [mobilePanel,  setMobilePanel]  = useState('list'); // list | detail

  // Compose state
  const [to,           setTo]           = useState('');
  const [cc,           setCc]           = useState('');
  const [subject,      setSubject]      = useState('');
  const [body,         setBody]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [sendOk,       setSendOk]       = useState(false);
  const [showCc,       setShowCc]       = useState(false);
  const [suggestions,  setSuggestions]  = useState([]);
  const [focusTo,      setFocusTo]      = useState(false);
  const [assignOnSend, setAssignOnSend] = useState(false);

  // Detail assign state
  const [assigning,    setAssigning]    = useState(false);
  const [assignDone,   setAssignDone]   = useState(false);

  const toRef = useRef(null);

  // Resize listener
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Load team members for autocomplete + assign
  useEffect(() => {
    return onSnapshot(collection(db(), 'authorized_users'), snap => {
      setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(m => m.active !== false && m.email));
    }, () => {});
  }, []);

  // Autocomplete suggestions
  useEffect(() => {
    if (!to.trim() || !focusTo) { setSuggestions([]); return; }
    const q = to.toLowerCase();
    setSuggestions(members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 6));
  }, [to, members, focusTo]);

  // Load messages when tab/token changes
  useEffect(() => {
    if (gmailToken && (tab === 'inbox' || tab === 'sent')) {
      setMessages([]); setNextToken(null); setViewMsg(null);
      loadMessages(false);
    }
  }, [tab, gmailToken]); // eslint-disable-line

  // ── Connect / Disconnect ───────────────────────────────────────────────────
  async function connectGmail() {
    setConnecting(true);
    try {
      const auth = getAuth(getApp());
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/gmail.send');
      provider.addScope('https://www.googleapis.com/auth/gmail.readonly');
      provider.addScope('https://www.googleapis.com/auth/gmail.modify');
      const result = await signInWithPopup(auth, provider);
      const cred = GoogleAuthProvider.credentialFromResult(result);
      const token = cred?.accessToken;
      if (!token) throw new Error('No access token returned.');
      storeToken(token, result.user.email);
      setGmailToken(token);
      setGmailEmail(result.user.email);
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') alert('Gmail connect failed: ' + e.message);
    }
    setConnecting(false);
  }

  function disconnect() {
    clearToken(); setGmailToken(null); setGmailEmail(''); setMessages([]); setViewMsg(null);
  }

  function handleTokenExpired() {
    clearToken(); setGmailToken(null);
    alert('Gmail session expired. Please reconnect.');
  }

  // ── Load messages ──────────────────────────────────────────────────────────
  async function loadMessages(loadMore = false) {
    setLoading(true);
    try {
      const label = tab === 'inbox' ? 'INBOX' : 'SENT';
      const pageParam = loadMore && nextToken ? `&pageToken=${nextToken}` : '';
      const data = await gmailFetch(`/messages?labelIds=${label}&maxResults=20${pageParam}`, gmailToken);
      setNextToken(data.nextPageToken || null);
      const list = data.messages || [];
      const details = await Promise.all(
        list.map(m => gmailFetch(`/messages/${m.id}?format=metadata&metadataHeaders=From,To,Subject,Date`, gmailToken))
      );
      setMessages(prev => loadMore ? [...prev, ...details] : details);
    } catch (e) {
      if (e.message === 'TOKEN_EXPIRED') handleTokenExpired();
      else alert('Failed to load messages: ' + e.message);
    }
    setLoading(false);
  }

  // ── Open message ───────────────────────────────────────────────────────────
  async function openMessage(msg) {
    setViewMsg(msg); setMsgBody(''); setAssignDone(false); setLoadingBody(true);
    if (isMobile) setMobilePanel('detail');
    try {
      const full = await gmailFetch(`/messages/${msg.id}?format=full`, gmailToken);
      setMsgBody(extractBody(full.payload));
    } catch (e) {
      if (e.message === 'TOKEN_EXPIRED') handleTokenExpired();
      else setMsgBody('<p style="color:#f43f5e">Failed to load message.</p>');
    }
    setLoadingBody(false);
  }

  function backToList() {
    setViewMsg(null); setMsgBody(''); setMobilePanel('list');
  }

  function replyTo(msg) {
    const fromAddr = hdr(msg.headers, 'From');
    const orig = hdr(msg.headers, 'Subject');
    setTo(fromAddr);
    setSubject(orig.startsWith('Re:') ? orig : `Re: ${orig}`);
    setBody(`\n\n--- Original message ---\nFrom: ${fromAddr}\nDate: ${hdr(msg.headers, 'Date')}\n`);
    setTab('compose'); setViewMsg(null);
    if (isMobile) setMobilePanel('detail');
  }

  // ── Send email ─────────────────────────────────────────────────────────────
  async function sendEmail(e) {
    e.preventDefault();
    if (!to.trim() || !subject.trim()) return;
    setSending(true); setSendOk(false);
    try {
      const raw = buildRawEmail({
        to: to.trim(), cc: cc.trim(), subject: subject.trim(),
        bodyHtml: body.replace(/\n/g, '<br>'), fromName: user.name, fromEmail: gmailEmail,
      });
      await gmailFetch('/messages/send', gmailToken, { method: 'POST', body: JSON.stringify({ raw }) });

      // Assign as task if toggled and recipient is an org member
      if (assignOnSend) {
        const recipEmail = extractEmail(to.trim());
        const member = members.find(m => m.email?.toLowerCase() === recipEmail);
        if (member) {
          await addDoc(collection(db(), 'tasks'), { ...actorStamp(),
            title: subject.trim(),
            description: `Email to: ${to.trim()}\n\n${body.trim().slice(0, 300)}`,
            status: 'pending', priority: 'medium',
            portal: member.portal || 'all',
            assignedTo: member.name, assignedToUid: member.uid,
            createdBy: user.name, createdByUid: user.uid,
            requiresProof: true, sourceType: 'manual',
            source: 'email', createdAt: serverTimestamp(),
          });
        }
      }

      setSendOk(true);
      setTo(''); setCc(''); setSubject(''); setBody(''); setShowCc(false); setAssignOnSend(false);
      setTimeout(() => { setSendOk(false); setTab('sent'); }, 2500);
    } catch (e) {
      if (e.message === 'TOKEN_EXPIRED') handleTokenExpired();
      else alert('Failed to send: ' + e.message);
    }
    setSending(false);
  }

  // ── Archive / Trash ────────────────────────────────────────────────────────
  async function archiveMsg(id) {
    try {
      await gmailFetch(`/messages/${id}/modify`, gmailToken, {
        method: 'POST', body: JSON.stringify({ removeLabelIds: ['INBOX'] }),
      });
      setMessages(p => p.filter(m => m.id !== id)); setViewMsg(null);
    } catch (e) { alert('Archive failed: ' + e.message); }
  }

  async function trashMsg(id) {
    try {
      await gmailFetch(`/messages/${id}/trash`, gmailToken, { method: 'POST' });
      setMessages(p => p.filter(m => m.id !== id)); setViewMsg(null);
    } catch (e) { alert('Trash failed: ' + e.message); }
  }

  // ── Assign email as task ───────────────────────────────────────────────────
  async function assignMsgAsTask(msg, member) {
    setAssigning(true);
    try {
      await addDoc(collection(db(), 'tasks'), { ...actorStamp(),
        title: hdr(msg.headers, 'Subject') || '(No subject)',
        description: `Email from: ${hdr(msg.headers, 'From')}\n\n${msg.snippet || ''}`,
        status: 'pending', priority: 'medium',
        portal: member.portal || 'all',
        assignedTo: member.name, assignedToUid: member.uid,
        createdBy: user.name, createdByUid: user.uid,
        requiresProof: true, sourceType: 'manual',
        source: 'email', emailId: msg.id, createdAt: serverTimestamp(),
      });
      setAssignDone(true);
    } catch (e) { alert('Assign failed: ' + e.message); }
    setAssigning(false);
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredMessages = messages.filter(msg => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      hdr(msg.headers, 'Subject').toLowerCase().includes(q) ||
      hdr(msg.headers, 'From').toLowerCase().includes(q) ||
      hdr(msg.headers, 'To').toLowerCase().includes(q) ||
      (msg.snippet || '').toLowerCase().includes(q)
    );
  });

  const unreadCount = messages.filter(m => (m.labelIds || []).includes('UNREAD')).length;

  function findOrgMember(msg) {
    if (!msg) return null;
    const email = tab === 'inbox'
      ? extractEmail(hdr(msg.headers, 'From'))
      : extractEmail(hdr(msg.headers, 'To'));
    return members.find(m => m.email?.toLowerCase() === email) || null;
  }

  const activeMember = viewMsg ? findOrgMember(viewMsg) : null;

  // Compose: detect if recipient is org member
  const recipEmail = to.trim() ? extractEmail(to.trim()) : '';
  const recipMember = recipEmail ? members.find(m => m.email?.toLowerCase() === recipEmail) : null;

  // ── Color tokens ───────────────────────────────────────────────────────────
  const sidebarBg = T.dark ? T.bg        : T.panel;
  const listBg    = T.dark ? T.panel     : `${T.border}18`;
  const detailBg  = T.dark ? T.panel2    : T.panel;
  const inputBg   = T.dark ? `${T.border}50` : `${T.border}30`;

  // ── NOT CONNECTED ──────────────────────────────────────────────────────────
  if (!gmailToken) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', fontFamily: T.sans }}>
        <div style={{ width: 72, height: 72, borderRadius: 20, background: `${T.accent}15`,
          border: `1px solid ${T.accent}40`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 34, marginBottom: 24 }}>✉️</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.bright, marginBottom: 8 }}>
          Connect your Gmail
        </div>
        <div style={{ fontSize: 13, color: T.muted, maxWidth: 400, textAlign: 'center',
          lineHeight: 1.8, marginBottom: 32 }}>
          Link your Google account to send and receive emails directly in the app.
          Your inbox and sent mail appear here and in Gmail simultaneously.
        </div>
        <button onClick={connectGmail} disabled={connecting}
          style={{ padding: '12px 32px', background: connecting ? 'transparent' : `${T.accent}15`,
            border: `1px solid ${T.accent}`, color: T.accent, fontFamily: T.mono, fontSize: 12,
            cursor: connecting ? 'wait' : 'pointer', borderRadius: 10, letterSpacing: 1,
            display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {connecting ? 'CONNECTING…' : 'CONNECT GMAIL'}
        </button>
      </div>
    );
  }

  // ── MAIN 3-PANEL UI ────────────────────────────────────────────────────────
  // Mobile: show one panel at a time (sidebar hidden, list ↔ detail/compose)
  const showSidebar = !isMobile;
  const showList    = !isMobile || (mobilePanel === 'list' && tab !== 'compose');
  const showDetail  = !isMobile || mobilePanel === 'detail' || tab === 'compose';

  return (
    <div style={{ display: 'flex', height: isMobile ? '85vh' : '78vh', minHeight: 480,
      fontFamily: T.sans, border: `1px solid ${T.border}`, borderRadius: 14,
      overflow: 'hidden', background: sidebarBg }}>

      {/* ══ SIDEBAR ══════════════════════════════════════════════════════════ */}
      {showSidebar && (
        <div style={{ width: 210, flexShrink: 0, background: sidebarBg,
          borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>

          {/* Brand */}
          <div style={{ padding: '18px 16px 12px', borderBottom: `1px solid ${T.border}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: `${T.accent}18`,
                border: `1px solid ${T.accent}40`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 20 }}>✉️</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: T.bright, letterSpacing: 0.5 }}>MAILBOX</div>
                <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginTop: 1, letterSpacing: 2 }}>
                  COMMUNICATIONS
                </div>
              </div>
            </div>
          </div>

          {/* Compose button */}
          <div style={{ padding: '12px 12px 6px' }}>
            <button onClick={() => { setTab('compose'); setViewMsg(null); }}
              style={{ width: '100%', padding: '10px 0', background: `${T.accent}15`,
                border: `1px solid ${T.accent}50`, color: T.accent, fontFamily: T.mono,
                fontSize: 11, cursor: 'pointer', borderRadius: 8, letterSpacing: 1,
                fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              ✏  COMPOSE
            </button>
          </div>

          {/* Nav items */}
          <nav style={{ padding: '6px 8px' }}>
            {[
              { id: 'inbox', icon: '📥', label: 'Inbox',  badge: unreadCount },
              { id: 'sent',  icon: '📤', label: 'Sent',   badge: 0 },
            ].map(n => (
              <button key={n.id} onClick={() => { setTab(n.id); setViewMsg(null); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  marginBottom: 2, background: tab === n.id ? `${T.accent}15` : 'transparent',
                  color: tab === n.id ? T.accent : T.text, fontFamily: T.sans, fontSize: 13,
                  fontWeight: tab === n.id ? 600 : 400, textAlign: 'left',
                  borderLeft: `3px solid ${tab === n.id ? T.accent : 'transparent'}` }}>
                <span style={{ fontSize: 15 }}>{n.icon}</span>
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.badge > 0 && (
                  <span style={{ background: T.accent, color: T.dark ? '#000' : '#fff',
                    fontFamily: T.mono, fontSize: 9, fontWeight: 700,
                    padding: '2px 7px', borderRadius: 20 }}>{n.badge}</span>
                )}
              </button>
            ))}

            {/* Team quick-compose */}
            {members.length > 0 && (
              <>
                <div style={{ height: 1, background: T.border, margin: '10px 4px' }} />
                <div style={{ padding: '4px 12px 6px', fontFamily: T.mono, fontSize: 9,
                  color: T.muted, letterSpacing: 2 }}>TEAM</div>
                {members.slice(0, 6).map(m => (
                  <button key={m.uid} onClick={() => {
                    setTab('compose'); setTo(m.email); setSubject(''); setBody(''); setViewMsg(null);
                  }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                      padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: 'transparent', color: T.text, fontFamily: T.sans, fontSize: 12,
                      textAlign: 'left', marginBottom: 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = `${T.accent}10`}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${T.accent}18`,
                      border: `1px solid ${T.accent}30`, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontFamily: T.mono, fontSize: 9,
                      color: T.accent, fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(m.name)}
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.name || m.email}
                    </span>
                  </button>
                ))}
              </>
            )}
          </nav>

          <div style={{ flex: 1 }} />

          {/* User card */}
          <div style={{ padding: '12px 14px', borderTop: `1px solid ${T.border}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${T.accent}20`,
                border: `1px solid ${T.accent}50`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontFamily: T.mono, fontSize: 12, color: T.accent,
                fontWeight: 700, flexShrink: 0 }}>
                {getInitials(user.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.bright,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gmailEmail}</div>
              </div>
              <button onClick={disconnect} title="Disconnect Gmail"
                style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer',
                  padding: 4, fontSize: 13, borderRadius: 4 }}>⏻</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ EMAIL LIST ═══════════════════════════════════════════════════════ */}
      {showList && (tab === 'inbox' || tab === 'sent') && (
        <div style={{ width: isMobile ? '100%' : 340, flexShrink: 0, background: listBg,
          borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column',
          overflow: 'hidden' }}>

          {/* Search */}
          <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${T.border}40` }}>
            {/* Mobile header */}
            {isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: T.bright, flex: 1 }}>
                  {tab === 'inbox' ? '📥 Inbox' : '📤 Sent'}
                </div>
                <button onClick={() => { setTab('compose'); setMobilePanel('detail'); }}
                  style={{ padding: '6px 12px', background: `${T.accent}15`, border: `1px solid ${T.accent}50`,
                    color: T.accent, fontFamily: T.mono, fontSize: 10, cursor: 'pointer', borderRadius: 6 }}>
                  ✏ Compose
                </button>
                <button onClick={() => { setTab(tab === 'inbox' ? 'sent' : 'inbox'); }}
                  style={{ padding: '6px 10px', background: 'transparent', border: `1px solid ${T.border}`,
                    color: T.muted, fontFamily: T.mono, fontSize: 10, cursor: 'pointer', borderRadius: 6 }}>
                  {tab === 'inbox' ? '📤' : '📥'}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: inputBg,
              border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 12px' }}>
              <span style={{ color: T.muted, fontSize: 13 }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search communications..."
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: T.text, fontFamily: T.sans, fontSize: 13 }} />
              {search && (
                <button onClick={() => setSearch('')}
                  style={{ background: 'none', border: 'none', color: T.muted,
                    cursor: 'pointer', fontSize: 12, padding: 0 }}>✕</button>
              )}
            </div>
          </div>

          {/* Section header */}
          <div style={{ padding: '8px 14px 6px', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: 3, flex: 1 }}>
              {tab.toUpperCase()}
              {search && ` — "${search}"`}
            </span>
            <button onClick={() => loadMessages(false)} disabled={loading}
              style={{ background: 'none', border: 'none', color: T.muted,
                cursor: loading ? 'default' : 'pointer', fontFamily: T.mono, fontSize: 12,
                padding: '2px 6px' }} title="Refresh">
              {loading ? '…' : '⟳'}
            </button>
          </div>

          {/* Message rows */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && filteredMessages.length === 0 && (
              <div style={{ padding: '50px 20px', textAlign: 'center',
                fontFamily: T.mono, fontSize: 10, color: T.muted }}>LOADING…</div>
            )}
            {!loading && filteredMessages.length === 0 && (
              <div style={{ padding: '50px 20px', textAlign: 'center',
                fontFamily: T.mono, fontSize: 10, color: T.muted }}>
                {search ? 'No results.' : tab === 'inbox' ? 'Inbox is empty.' : 'No sent messages.'}
              </div>
            )}

            {filteredMessages.map((msg, i) => {
              const from    = hdr(msg.headers, 'From');
              const to_     = hdr(msg.headers, 'To');
              const subj    = hdr(msg.headers, 'Subject') || '(No subject)';
              const dateStr = hdr(msg.headers, 'Date');
              const isUnread = (msg.labelIds || []).includes('UNREAD');
              const isActive = viewMsg?.id === msg.id;
              const displayName = (tab === 'inbox' ? from : to_)
                .replace(/<.*?>/g, '').trim() || (tab === 'inbox' ? from : to_);

              return (
                <div key={msg.id || i} onClick={() => openMessage(msg)}
                  style={{ padding: '12px 14px', cursor: 'pointer',
                    background: isActive ? `${T.accent}12` : 'transparent',
                    borderBottom: `1px solid ${T.border}40`,
                    borderLeft: `3px solid ${isActive ? T.accent : isUnread ? T.accent + '70' : 'transparent'}`,
                    transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = `${T.accent}08`; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {/* Avatar */}
                    <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: `${T.accent}18`, border: `1px solid ${T.accent}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: T.mono, fontSize: 12, color: T.accent, fontWeight: 700 }}>
                      {getInitials(tab === 'inbox' ? from : to_)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'baseline', gap: 4, marginBottom: 2 }}>
                        <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13, color: T.bright,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {displayName}
                        </span>
                        <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, flexShrink: 0 }}>
                          {relTime(dateStr)}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: isUnread ? 600 : 400,
                        color: isUnread ? T.text : T.muted, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{subj}</div>
                      <div style={{ fontSize: 11, color: T.muted, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.snippet || ''}</div>
                    </div>
                    {isUnread && (
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.accent,
                        flexShrink: 0, marginTop: 7 }} />
                    )}
                  </div>
                </div>
              );
            })}

            {/* Load more */}
            {nextToken && !loading && (
              <div style={{ padding: '14px', textAlign: 'center' }}>
                <button onClick={() => loadMessages(true)}
                  style={{ padding: '8px 24px', background: 'transparent',
                    border: `1px solid ${T.border}`, color: T.muted,
                    fontFamily: T.mono, fontSize: 10, cursor: 'pointer', borderRadius: 6 }}>
                  LOAD MORE
                </button>
              </div>
            )}
            {loading && filteredMessages.length > 0 && (
              <div style={{ padding: '14px', textAlign: 'center',
                fontFamily: T.mono, fontSize: 10, color: T.muted }}>Loading…</div>
            )}
          </div>
        </div>
      )}

      {/* ══ DETAIL / COMPOSE PANEL ═══════════════════════════════════════════ */}
      {showDetail && (
        <div style={{ flex: 1, background: detailBg, display: 'flex',
          flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* ── MESSAGE DETAIL ── */}
          {viewMsg && tab !== 'compose' && (() => {
            const subj    = hdr(viewMsg.headers, 'Subject') || '(No subject)';
            const from    = hdr(viewMsg.headers, 'From');
            const to_     = hdr(viewMsg.headers, 'To');
            const dateStr = hdr(viewMsg.headers, 'Date');
            return (
              <>
                {/* Toolbar */}
                <div style={{ padding: '11px 16px', borderBottom: `1px solid ${T.border}40`,
                  display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                  {isMobile && (
                    <button onClick={backToList}
                      style={{ background: 'none', border: 'none', color: T.accent, cursor: 'pointer',
                        fontFamily: T.mono, fontSize: 10, letterSpacing: 1, padding: '4px 8px 4px 0' }}>
                      ← BACK
                    </button>
                  )}
                  <button onClick={() => archiveMsg(viewMsg.id)} title="Archive"
                    style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted,
                      cursor: 'pointer', padding: '6px 10px', borderRadius: 7, fontSize: 14, lineHeight: 1 }}>
                    🗄
                  </button>
                  <button onClick={() => trashMsg(viewMsg.id)} title="Move to trash"
                    style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted,
                      cursor: 'pointer', padding: '6px 10px', borderRadius: 7, fontSize: 14, lineHeight: 1 }}>
                    🗑
                  </button>
                  <div style={{ flex: 1 }} />

                  {/* Assign to team member button */}
                  {activeMember && !assignDone && (
                    <button onClick={() => assignMsgAsTask(viewMsg, activeMember)} disabled={assigning}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px',
                        background: `${T.accent}18`, border: `1px solid ${T.accent}`,
                        color: T.accent, fontFamily: T.mono, fontSize: 10, letterSpacing: 0.5,
                        cursor: assigning ? 'wait' : 'pointer', borderRadius: 8,
                        fontWeight: 700, whiteSpace: 'nowrap' }}>
                      📋 {assigning ? 'Assigning…' : `Assign to: ${activeMember.name.split(' ')[0]}`}
                    </button>
                  )}
                  {assignDone && (
                    <span style={{ fontFamily: T.mono, fontSize: 10, color: T.green,
                      display: 'flex', alignItems: 'center', gap: 5 }}>✓ Task assigned</span>
                  )}

                  <button onClick={() => replyTo(viewMsg)}
                    style={{ padding: '7px 14px', background: 'transparent',
                      border: `1px solid ${T.border}`, color: T.muted,
                      fontFamily: T.mono, fontSize: 10, cursor: 'pointer', borderRadius: 7 }}>
                    ↩ Reply
                  </button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '28px 32px' }}>
                  {/* Large subject */}
                  <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: T.bright,
                    margin: '0 0 22px', lineHeight: 1.3, letterSpacing: -0.5 }}>{subj}</h2>

                  {/* Sender card */}
                  <div style={{ background: T.dark ? `${T.border}30` : `${T.border}35`,
                    border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px',
                    display: 'flex', gap: 12, marginBottom: 26 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                      background: `${T.accent}20`, border: `1px solid ${T.accent}40`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: T.mono, fontSize: 13, color: T.accent, fontWeight: 800 }}>
                      {getInitials(tab === 'inbox' ? from : to_)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: T.bright }}>
                            {(tab === 'inbox' ? from : to_).replace(/<.*?>/g, '').trim() || (tab === 'inbox' ? from : to_)}
                          </div>
                          <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, marginTop: 2 }}>
                            {tab === 'inbox'
                              ? `To: ${to_.replace(/<.*?>/g, '').trim() || to_}`
                              : `From: ${from.replace(/<.*?>/g, '').trim() || from}`}
                          </div>
                        </div>
                        <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, flexShrink: 0 }}>
                          {relTime(dateStr)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email body */}
                  {loadingBody ? (
                    <div style={{ fontFamily: T.mono, fontSize: 11, color: T.muted }}>Loading message…</div>
                  ) : (
                    <div style={{ color: T.text, lineHeight: 1.9, fontSize: 14, overflowX: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: msgBody || `<em style="opacity:0.4">Empty message</em>` }} />
                  )}
                </div>
              </>
            );
          })()}

          {/* ── COMPOSE ── */}
          {tab === 'compose' && (
            <form onSubmit={sendEmail} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.border}40`,
                display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {isMobile && (
                  <button type="button"
                    onClick={() => { setTab('inbox'); setMobilePanel('list'); }}
                    style={{ background: 'none', border: 'none', color: T.accent,
                      cursor: 'pointer', fontFamily: T.mono, fontSize: 10, letterSpacing: 1 }}>
                    ← BACK
                  </button>
                )}
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.accent, letterSpacing: 2, flex: 1 }}>
                  ✏  NEW MESSAGE
                </span>
                <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted }}>from {gmailEmail}</span>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {/* To */}
                <div style={{ borderBottom: `1px solid ${T.border}40`, padding: '10px 18px', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, width: 54, flexShrink: 0 }}>To</span>
                    <input ref={toRef} value={to} onChange={e => setTo(e.target.value)}
                      onFocus={() => setFocusTo(true)} onBlur={() => setTimeout(() => setFocusTo(false), 150)}
                      placeholder="recipient@example.com or team member name"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                        color: T.bright, fontFamily: T.sans, fontSize: 14 }} />
                    <button type="button" onClick={() => setShowCc(s => !s)}
                      style={{ fontFamily: T.mono, fontSize: 9, background: 'transparent',
                        border: 'none', color: T.muted, cursor: 'pointer', padding: '2px 6px' }}>Cc</button>
                  </div>
                  {/* Autocomplete */}
                  {focusTo && suggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: T.panel, border: `1px solid ${T.border}`,
                      boxShadow: '0 10px 30px #00000040', borderRadius: '0 0 10px 10px' }}>
                      {suggestions.map(m => (
                        <div key={m.uid} onClick={() => { setTo(m.email); setSuggestions([]); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px',
                            cursor: 'pointer', borderBottom: `1px solid ${T.border}20` }}
                          onMouseEnter={e => e.currentTarget.style.background = `${T.accent}10`}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%',
                            background: `${T.accent}18`, border: `1px solid ${T.accent}30`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: T.mono, fontSize: 11, color: T.accent }}>
                            {getInitials(m.name)}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.bright }}>{m.name}</div>
                            <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted }}>{m.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cc */}
                {showCc && (
                  <div style={{ borderBottom: `1px solid ${T.border}40`, padding: '10px 18px',
                    display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, width: 54, flexShrink: 0 }}>Cc</span>
                    <input value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@example.com"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                        color: T.bright, fontFamily: T.sans, fontSize: 14 }} />
                  </div>
                )}

                {/* Subject */}
                <div style={{ borderBottom: `1px solid ${T.border}40`, padding: '10px 18px',
                  display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, width: 54, flexShrink: 0 }}>Subject</span>
                  <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      color: T.bright, fontFamily: T.sans, fontSize: 14, fontWeight: 600 }} />
                </div>

                {/* Body */}
                <textarea value={body} onChange={e => setBody(e.target.value)}
                  placeholder="Write your message here…"
                  style={{ flex: 1, width: '100%', background: 'transparent', border: 'none', outline: 'none',
                    resize: 'none', color: T.text, fontFamily: T.sans, fontSize: 14, lineHeight: 1.8,
                    padding: '16px 18px', boxSizing: 'border-box', minHeight: 180 }} />
              </div>

              {/* Footer */}
              <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.border}40`,
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
                <button type="submit" disabled={sending || !to.trim() || !subject.trim()}
                  style={{ padding: '10px 24px',
                    background: (sending || !to.trim() || !subject.trim()) ? 'transparent' : `${T.accent}18`,
                    border: `1px solid ${(sending || !to.trim() || !subject.trim()) ? T.border : T.accent}`,
                    color: (sending || !to.trim() || !subject.trim()) ? T.muted : T.accent,
                    fontFamily: T.mono, fontSize: 11, letterSpacing: 1,
                    cursor: sending ? 'wait' : 'pointer', borderRadius: 7 }}>
                  {sending ? 'SENDING…' : '▶  SEND'}
                </button>

                {/* Assign as task toggle — only shown when recipient is an org member */}
                {recipMember && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '7px 12px', borderRadius: 8,
                    background: assignOnSend ? `${T.accent}12` : 'transparent',
                    border: `1px solid ${assignOnSend ? T.accent : T.border}`,
                    transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={assignOnSend}
                      onChange={e => setAssignOnSend(e.target.checked)}
                      style={{ accentColor: T.accent, cursor: 'pointer' }} />
                    <span style={{ fontFamily: T.mono, fontSize: 10,
                      color: assignOnSend ? T.accent : T.muted }}>
                      📋 Assign as task to {recipMember.name.split(' ')[0]}
                    </span>
                  </label>
                )}

                {sendOk && (
                  <span style={{ fontFamily: T.mono, fontSize: 10, color: T.green }}>
                    ✓ Sent{assignOnSend && recipMember ? ' + task assigned' : ''}
                  </span>
                )}

                <div style={{ flex: 1 }} />
                <button type="button"
                  onClick={() => { setTo(''); setCc(''); setSubject(''); setBody(''); setShowCc(false); setAssignOnSend(false); }}
                  style={{ fontFamily: T.mono, fontSize: 9, background: 'transparent',
                    border: 'none', color: T.muted, cursor: 'pointer' }}>
                  Discard
                </button>
              </div>
            </form>
          )}

          {/* ── EMPTY STATE (no message selected, not composing) ── */}
          {!viewMsg && tab !== 'compose' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 14, padding: 24 }}>
              <div style={{ fontSize: 52, opacity: 0.2 }}>✉️</div>
              <div style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, letterSpacing: 2 }}>
                SELECT A MESSAGE TO READ
              </div>
              <button onClick={() => { setTab('compose'); }}
                style={{ marginTop: 8, padding: '9px 22px', background: `${T.accent}15`,
                  border: `1px solid ${T.accent}50`, color: T.accent, fontFamily: T.mono,
                  fontSize: 10, cursor: 'pointer', borderRadius: 8, letterSpacing: 1 }}>
                ✏ COMPOSE NEW
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
